import React, { useEffect, useState, useContext } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import Modal from "react-modal";
import clsx from "clsx";
import moment from "moment-timezone";
import { useMutation, useQuery } from "@apollo/client";

import { AppContext } from "contexts/AppContextProvider";
import { QueryBuilderContext } from "contexts/QueryBuilderContextProvider";
import client from "client";
import {
  DELETE_NAMED_QUERY,
  LIST_SAVED_QUERIES,
  SAVE_QUERY,
} from "components/QueryBuilder/QueryBuilderQueries";
import { fuzzySearch } from "components/Utils/Utils";
import { formatDateTimelapse } from "components/Utils/DateTimeUtils";
import PiButton from "components/Elements/PiButton";

import { FaRegCopy } from "react-icons/fa";
import { IoIosClose } from "react-icons/io";
import {
  HiOutlineLockClosed,
  HiOutlineUserGroup,
  HiOutlineTrash,
} from "react-icons/hi";

const QueryLoadQueries = (props) => {
  // Contexts
  const { state: configState } = useContext(AppContext);
  const { originalQueryStructure } = useContext(QueryBuilderContext).state;

  // State variables
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [filterBy, setFilterBy] = useState(undefined);
  const [selectedQuery, setSelectedQuery] = useState(undefined);

  // Update moment locale when modal is open
  useEffect(() => {
    if (showModal) {
      moment.updateLocale("en", {
        relativeTime: {
          future: "%s",
          past: "%s",
          s: "A few seconds ago",
          ss: "%d seconds ago",
          m: "A minute ago",
          mm: "%d minutes ago",
          h: "An hour ago",
          hh: "Today",
          d: "Yesterday",
        },
      });
    } else {
      moment.updateLocale("en", null); // Revert to default locale
    }
  }, [showModal]);

  // Fetch saved queries when modal is open
  const { data, refetch } = useQuery(LIST_SAVED_QUERIES, {
    skip: !showModal,
    client,
    fetchPolicy: "network-only",
    variables: { tenantID: parseInt(localStorage.defaultTenantID) },
  });

  // Mutations
  const [deleteQueryById] = useMutation(DELETE_NAMED_QUERY, {
    onCompleted: () => {
      refetch();
      if (props.savedQuery?.id === selectedQuery) {
        props.onDeleteQuery?.();
      }
      setSelectedQuery(undefined);
    },
  });

  const [saveShortUrl] = useMutation(SAVE_QUERY);

  // Table columns
  const columns = [
    {
      dataField: "name",
      text: "Name",
    },
    {
      dataField: "author",
      text: "Created By",
      formatter: (cell, row) => {
        const isCurrentUser = row.author?.email === localStorage.email;
        return (
          <div className="creator">
            {isCurrentUser ? <HiOutlineLockClosed /> : <HiOutlineUserGroup />}
            {isCurrentUser ? "You" : row.author?.firstName}
          </div>
        );
      },
    },
    {
      dataField: "updatedAt",
      text: "Last Modified",
      formatter: (cell, row) => {
        const isCurrentUser = row.author?.email === localStorage.email;
        return (
          <div className="delete-btn">
            {formatDateTimelapse(cell)}
            {isCurrentUser && (
              <HiOutlineTrash onClick={() => setSelectedQuery(row.id)} />
            )}
          </div>
        );
      },
    },
  ];

  // Get filtered and sorted data
  const getFilteredData = () => {
    const projects = data?.projects || [];
    const currentProjectID = configState.currentProject.projectObj.id;
    const currentProject = projects.find(
      (project) => project.id === currentProjectID
    );
    const namedQueries = currentProject?.namedQueries || [];

    let filteredQueries = namedQueries;

    // Apply filters
    if (filterBy !== undefined) {
      filteredQueries = filteredQueries.filter(
        (query) => query.private === filterBy
      );
    }

    if (searchText) {
      filteredQueries = filteredQueries.filter((query) =>
        fuzzySearch(searchText, query.name)
      );
    }

    // Sort and add friendly name
    return filteredQueries
      .map((query) => ({ ...query, friendlyName: query.name }))
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  };

  const filteredData = getFilteredData();

  // Load selected query
  const loadQuery = (queryData) => {
    const queryString = getParsedQueryString(queryData);

    saveShortUrl({
      variables: {
        tenantID: parseInt(localStorage.defaultTenantID),
        query: queryString,
        originalQuery: originalQueryStructure || "",
      },
    }).then(({ data: savedShortUrl }) => {
      window.history.replaceState(null, null, "#" + savedShortUrl.saveQuery);
      props.setNewQuery(queryString);
    });
  };

  // Parse query string
  const getParsedQueryString = (queryData) => {
    const decodedQuery = queryData.query.includes("%20")
      ? decodeURIComponent(queryData.query)
      : queryData.query;

    const parsedQuery = JSON.parse(decodedQuery).filter(
      (param) => !param.savedQuery
    );

    const { __typename, friendlyName, ...cleanedQueryData } = queryData;

    parsedQuery.push({ savedQuery: cleanedQueryData });

    return JSON.stringify(parsedQuery);
  };

  // Handle row click
  const handleRowClick = (event, row) => {
    if (!["svg", "path"].includes(event.target.nodeName.toLowerCase())) {
      loadQuery(row);
    }
  };

  // Confirm query deletion
  const handleQueryDelete = () => {
    deleteQueryById({
      variables: {
        id: selectedQuery,
        tenantID: parseInt(localStorage.defaultTenantID),
      },
    });
  };

  return (
    <>
      {/* Button to open modal */}
      <button
        className={clsx(
          "query-option-button h-100 w-80 query-option-button--border query-option-button--strong-label animated fadeIn",
          { "query-active-button": showModal }
        )}
        onClick={() => setShowModal(!showModal)}
      >
        Saved Queries <FaRegCopy />
      </button>

      {/* Modal for saved queries */}
      <Modal
        isOpen={showModal}
        ariaHideApp={false}
        overlayClassName="modal__overlay"
        className="modal__window"
        onRequestClose={() => setShowModal(false)}
      >
        <div className="container-fluid qb-load-query">
          {/* Header */}
          <div className="row">
            <div className="col-11">
              <h4 className="title">Browse Saved Queries</h4>
            </div>
            <div className="col-1 text-right">
              <IoIosClose
                className="close-browser"
                onClick={() => setShowModal(false)}
              />
            </div>
          </div>

          {/* Search and Filters */}
          <div className="row mt-3">
            <div className="col-5">
              <input
                type="text"
                className="search-input"
                placeholder="Search for"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="col-7 text-right filters">
              <span>Filter by</span>
              <button
                className={clsx({ selected: filterBy === undefined })}
                onClick={() => setFilterBy(undefined)}
              >
                All
              </button>
              <button
                className={clsx("btn-private", { selected: filterBy === true })}
                onClick={() => setFilterBy(true)}
              >
                <HiOutlineLockClosed /> Private
              </button>
              <button
                className={clsx("btn-shared", { selected: filterBy === false })}
                onClick={() => setFilterBy(false)}
              >
                <HiOutlineUserGroup /> Shared
              </button>
            </div>
          </div>

          {/* Table */}
          <BootstrapTable
            keyField="id"
            classes="mt-3"
            data={filteredData}
            columns={columns}
            rowEvents={{ onClick: handleRowClick }}
          />
        </div>

        {/* Delete Confirmation */}
        <Modal
          isOpen={!!selectedQuery}
          ariaHideApp={false}
          overlayClassName="dialog__overlay"
          className="dialog__window"
          onRequestClose={() => setSelectedQuery(undefined)}
        >
          <div className="row">
            <div className="col-12 text-center">
              <h4>
                Are you sure you want to delete this query? It will no longer be
                accessible to anyone.
              </h4>
              <div className="pt-2 pi-button-container">
                <PiButton onClick={handleQueryDelete}>Delete</PiButton>
                <PiButton onClick={() => setSelectedQuery(undefined)}>
                  Cancel
                </PiButton>
              </div>
            </div>
          </div>
        </Modal>
      </Modal>
    </>
  );
};

export default QueryLoadQueries;
